// Règles des conseils (M16, lot L5-B, D-222, D-240) : quorum, majorité, mode de vote, procurations et
// délai de convocation sont des paramètres administratifs explicitement renseignés et versionnés par la
// collectivité ; aucune valeur n'est préremplie ni présentée comme règle officielle du Gabon (à confirmer
// par l'administration). Calculs purs partagés avec le serveur et les écrans ; la base refait le contrôle
// du quorum et des totaux (migration 0112), à partir de l'instantané de la règle conservé par la session.
import { z } from 'zod';

export const BASES_MAJORITE = {
  suffrages_exprimes: 'Suffrages exprimés (pour et contre)',
  presents_et_representes: 'Membres présents et représentés',
  membres_en_exercice: 'Membres en exercice',
} as const;
export type BaseMajorite = keyof typeof BASES_MAJORITE;

export const MODES_VOTE = { nominatif: 'Vote nominatif (choix de chaque élu)', agrege: 'Vote à main levée (totaux seulement)' } as const;
export type ModeVote = keyof typeof MODES_VOTE;

const fraction = z.object({
  numerateur: z.number().int().min(1).max(100),
  denominateur: z.number().int().min(1).max(100),
  /** Vrai : strictement plus que la fraction ; faux : au moins la fraction. */
  strict: z.boolean(),
});

/** Paramètres d'une règle : tous obligatoires, aucun défaut (D-222). */
export const schemaParametresRegle = z
  .object({
    quorum: fraction.extend({ procurations_comptent: z.boolean() }),
    majorite: fraction.extend({ base: z.enum(['suffrages_exprimes', 'presents_et_representes', 'membres_en_exercice']), voix_preponderante_president: z.boolean() }),
    vote: z.object({ mode: z.enum(['nominatif', 'agrege']) }),
    procurations: z.object({ autorisees: z.boolean(), max_par_mandataire: z.number().int().min(0).max(5) }),
    convocation: z.object({ delai_jours: z.number().int().min(0).max(60) }),
  })
  .refine((r) => r.quorum.numerateur <= r.quorum.denominateur && r.majorite.numerateur <= r.majorite.denominateur, {
    message: 'Une fraction ne dépasse pas 1',
    path: ['quorum'],
  })
  .refine((r) => r.procurations.autorisees || r.procurations.max_par_mandataire === 0, {
    message: 'Sans procuration autorisée, la limite par mandataire vaut 0',
    path: ['procurations', 'max_par_mandataire'],
  })
  .refine((r) => !r.procurations.autorisees || r.procurations.max_par_mandataire >= 1, {
    message: 'Une procuration autorisée exige une limite d\'au moins 1 par mandataire',
    path: ['procurations', 'max_par_mandataire'],
  });
export type ParametresRegle = z.infer<typeof schemaParametresRegle>;

/** Seuil entier d'une fraction d'un effectif : strictement plus (plancher + 1) ou au moins (plafond). */
export function seuilFraction(effectif: number, f: { numerateur: number; denominateur: number; strict: boolean }): number {
  if (effectif <= 0) return f.strict ? 1 : 0;
  const produit = effectif * f.numerateur;
  return f.strict ? Math.floor(produit / f.denominateur) + 1 : Math.ceil(produit / f.denominateur);
}

export interface EtatQuorum {
  membres_en_exercice: number;
  presents: number;
  representes: number;
  /** Présents, plus les représentés si la règle le prévoit. */
  compte: number;
  requis: number;
  atteint: boolean;
}

/** Quorum selon la règle : compte des présents (et représentés si prévu) au regard des membres en exercice. */
export function calculerQuorum(r: ParametresRegle, membres: number, presents: number, representes: number): EtatQuorum {
  const requis = seuilFraction(membres, r.quorum);
  const compte = presents + (r.quorum.procurations_comptent ? representes : 0);
  return { membres_en_exercice: membres, presents, representes, compte, requis, atteint: membres > 0 && compte >= requis };
}

export interface TotauxVote {
  pour: number;
  contre: number;
  abstention: number;
  ne_prend_pas_part: number;
}

export type ResultatVote = 'adoptee' | 'rejetee';

/** Contrôle de cohérence : votants (totaux) au plus égaux aux voix autorisées (présents et représentés). */
export function controlerTotaux(t: TotauxVote, voixAutorisees: number): string | null {
  const valeurs = [t.pour, t.contre, t.abstention, t.ne_prend_pas_part];
  if (valeurs.some((v) => !Number.isInteger(v) || v < 0)) return 'Les totaux sont des entiers positifs ou nuls';
  const votants = valeurs.reduce((s, v) => s + v, 0);
  if (votants === 0) return 'Aucune voix enregistrée';
  if (votants > voixAutorisees) return `Plus de voix (${votants}) que de votants autorisés (${voixAutorisees})`;
  return null;
}

/** Résultat d'un vote : seuil de majorité sur la base choisie ; partage tranché par la voix du président si prévu. */
export function resultatVote(
  r: ParametresRegle,
  t: TotauxVote,
  ctx: { membres: number; presents: number; representes: number; voix_president: 'pour' | 'contre' | 'abstention' | 'ne_prend_pas_part' | null },
): ResultatVote {
  const base = r.majorite.base === 'suffrages_exprimes' ? t.pour + t.contre : r.majorite.base === 'presents_et_representes' ? ctx.presents + ctx.representes : ctx.membres;
  if (base <= 0) return 'rejetee';
  if (r.majorite.voix_preponderante_president && r.majorite.base === 'suffrages_exprimes' && t.pour === t.contre && t.pour > 0) {
    return ctx.voix_president === 'pour' ? 'adoptee' : 'rejetee';
  }
  const seuil = r.majorite.strict ? t.pour * r.majorite.denominateur > base * r.majorite.numerateur : t.pour * r.majorite.denominateur >= base * r.majorite.numerateur;
  return seuil ? 'adoptee' : 'rejetee';
}

/** Date limite d'envoi de la convocation : date de séance moins le délai (jours calendaires). */
export function dateLimiteConvocation(dateSeance: string, delaiJours: number): string {
  const d = new Date(`${dateSeance.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - delaiJours);
  return d.toISOString().slice(0, 10);
}

/** Texte lisible d'une fraction (« plus de 1/2 », « au moins 2/3 »). */
export function texteFraction(f: { numerateur: number; denominateur: number; strict: boolean }): string {
  return `${f.strict ? 'plus de' : 'au moins'} ${f.numerateur}/${f.denominateur}`;
}
